import { useState, useEffect } from "react";
import {
  Plus,
  Percent,
  Clock,
  Tag,
  ToggleLeft,
  ToggleRight,
  Pencil,
  Trash2,
  Loader2,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import {
  usePromotions,
  useCreatePromotion,
  useUpdatePromotion,
  useTogglePromotion,
  useDeletePromotion,
} from "../api";
import { useProducts } from "@/features/products/api";
import { usePermissions } from "@/stores";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TimePicker,
  Checkbox,
} from "@/components/ui";
import {
  PageHeader,
  PageLoader,
  PageError,
  SearchablePicker,
} from "@/components/common";
import type {
  Promotion,
  CreatePromotionRequest,
  UpdatePromotionRequest,
} from "@/types";

const DAYS_OF_WEEK = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

export function PromotionsPage() {
  const { data, isLoading, error, refetch } = usePromotions({ limit: 100 });
  const { data: productsData } = useProducts({
    status: "active",
    limit: 500,
    all: true,
  });
  const productNames = (productsData?.data ?? []).reduce<Record<string, string>>(
    (acc, p) => {
      acc[p.id] = p.name;

      return acc;
    },
    {},
  );
  const createPromotion = useCreatePromotion();
  const updatePromotion = useUpdatePromotion();
  const togglePromotion = useTogglePromotion();
  const deletePromotion = useDeletePromotion();
  const { hasPermission } = usePermissions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<Promotion | null>(null);

  const canManage = hasPermission("settings.update");

  const handleCreate = () => {
    setEditingPromotion(null);
    setDialogOpen(true);
  };

  const handleEdit = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setDialogOpen(true);
  };

  const handleToggle = async (promotion: Promotion) => {
    try {
      await togglePromotion.mutateAsync(promotion.id);
      toast.success(
        promotion.isActive ? "Promotion deactivated" : "Promotion activated",
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to toggle promotion",
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePromotion.mutateAsync(deleteTarget.id);
      toast.success("Promotion deleted");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete promotion",
      );
    }
  };

  const handleSubmit = async (
    data: CreatePromotionRequest | UpdatePromotionRequest,
  ) => {
    try {
      if (editingPromotion) {
        await updatePromotion.mutateAsync({
          id: editingPromotion.id,
          data: data as UpdatePromotionRequest,
        });
        toast.success("Promotion updated");
      } else {
        await createPromotion.mutateAsync(data as CreatePromotionRequest);
        toast.success("Promotion created");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save promotion",
      );
    }
  };

  if (isLoading) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load promotions" onRetry={refetch} />;

  const promotions = data?.data ?? [];
  const discountCodes = promotions.filter((p) => p.type === "discount_code");
  const happyHours = promotions.filter((p) => p.type === "happy_hour");
  const bxgyPromos = promotions.filter((p) => p.type === "buy_x_get_y");

  return (
    <div className="container mx-auto p-6">
      <PageHeader
        title="Promotions"
        description="Manage discount codes and happy hour specials"
        actions={
          canManage && (
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Promotion
            </Button>
          )
        }
      />
      <div className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Discount Codes
              {discountCodes.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {discountCodes.length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Promo codes customers can enter at checkout
            </CardDescription>
          </CardHeader>
          <CardContent>
            {discountCodes.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No discount codes yet
              </p>
            ) : (
              <div className="space-y-3">
                {discountCodes.map((promo) => (
                  <PromotionRow
                    key={promo.id}
                    promotion={promo}
                    productNames={productNames}
                    onEdit={() => handleEdit(promo)}
                    onToggle={() => handleToggle(promo)}
                    onDelete={() => setDeleteTarget(promo)}
                    canManage={canManage}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Happy Hours
              {happyHours.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {happyHours.length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Time-based discounts applied automatically
            </CardDescription>
          </CardHeader>
          <CardContent>
            {happyHours.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No happy hours configured
              </p>
            ) : (
              <div className="space-y-3">
                {happyHours.map((promo) => (
                  <PromotionRow
                    key={promo.id}
                    promotion={promo}
                    productNames={productNames}
                    onEdit={() => handleEdit(promo)}
                    onToggle={() => handleToggle(promo)}
                    onDelete={() => setDeleteTarget(promo)}
                    canManage={canManage}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5" />
              Buy X Get Y Free
              {bxgyPromos.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {bxgyPromos.length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Item-based deals applied automatically when a product is bought
              in qualifying quantities
            </CardDescription>
          </CardHeader>
          <CardContent>
            {bxgyPromos.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No bundle deals configured
              </p>
            ) : (
              <div className="space-y-3">
                {bxgyPromos.map((promo) => (
                  <PromotionRow
                    key={promo.id}
                    promotion={promo}
                    productNames={productNames}
                    onEdit={() => handleEdit(promo)}
                    onToggle={() => handleToggle(promo)}
                    onDelete={() => setDeleteTarget(promo)}
                    canManage={canManage}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <PromotionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        promotion={editingPromotion}
        onSubmit={handleSubmit}
        isPending={createPromotion.isPending || updatePromotion.isPending}
      />
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete promotion?</DialogTitle>
            <DialogDescription>
              {deleteTarget &&
                `"${deleteTarget.name}" will be removed permanently. Past orders that used it keep the recorded discount.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deletePromotion.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deletePromotion.isPending}
            >
              {deletePromotion.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface PromotionRowProps {
  promotion: Promotion;
  productNames: Record<string, string>;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  canManage: boolean;
}

function PromotionRow({
  promotion,
  productNames,
  onEdit,
  onToggle,
  onDelete,
  canManage,
}: PromotionRowProps) {
  const formatDiscount = () => {
    if (promotion.type === "buy_x_get_y") {
      const buyName =
        (promotion.productId && productNames[promotion.productId]) ||
        "product";
      const freeName =
        (promotion.freeProductId && productNames[promotion.freeProductId]) ||
        "product";
      const same = promotion.productId === promotion.freeProductId;
      if (same) {
        return `Buy ${promotion.buyQuantity ?? "?"} ${buyName}, get ${promotion.getQuantity ?? "?"} free`;
      }

      return `Buy ${promotion.buyQuantity ?? "?"} ${buyName} → ${promotion.getQuantity ?? "?"} ${freeName} free`;
    }
    if (promotion.discountType === "percentage") {
      return `${promotion.discountValue}% off`;
    }

    return `$${(promotion.discountValue / 100).toFixed(2)} off`;
  };

  const formatTimeWindow = () => {
    if (promotion.startTime && promotion.endTime) {
      return `${promotion.startTime} - ${promotion.endTime}`;
    }
    if (promotion.type === "happy_hour") {
      return "All day";
    }

    return null;
  };

  const formatDays = () => {
    if (!promotion.daysOfWeek) return null;
    const days = promotion.daysOfWeek.split(",");
    if (days.length === 7) return "Every day";

    return days
      .map((d) =>
        DAYS_OF_WEEK.find((day) => day.value === d)?.label.slice(0, 3),
      )
      .filter(Boolean)
      .join(", ");
  };

  return (
    <div className="flex items-center justify-between rounded-lg border p-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{promotion.name}</span>
          <Badge variant={promotion.isActive ? "default" : "secondary"}>
            {promotion.isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            {promotion.type === "buy_x_get_y" ? (
              <Gift className="h-3 w-3" />
            ) : (
              <Percent className="h-3 w-3" />
            )}
            {formatDiscount()}
          </span>
          {promotion.code && (
            <Badge variant="outline" className="font-mono">
              {promotion.code}
            </Badge>
          )}
          {formatTimeWindow() && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatTimeWindow()}
            </span>
          )}
          {formatDays() && <span>({formatDays()})</span>}
          {promotion.maxUses && (
            <span>
              {promotion.usageCount}/{promotion.maxUses} uses
            </span>
          )}
        </div>
      </div>
      {canManage && (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onToggle}>
            {promotion.isActive ? (
              <ToggleRight className="h-5 w-5 text-green-600" />
            ) : (
              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

interface PromotionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promotion: Promotion | null;
  onSubmit: (data: CreatePromotionRequest | UpdatePromotionRequest) => void;
  isPending: boolean;
}

function PromotionDialog({
  open,
  onOpenChange,
  promotion,
  onSubmit,
  isPending,
}: PromotionDialogProps) {
  const [formData, setFormData] = useState<Partial<CreatePromotionRequest>>({
    name: "",
    type: "discount_code",
    discountType: "percentage",
    discountValue: 10,
    code: "",
    minOrderAmount: undefined,
    maxDiscount: undefined,
    startTime: "",
    endTime: "",
    daysOfWeek: "",
    maxUses: undefined,
    buyQuantity: undefined,
    getQuantity: undefined,
    productId: undefined,
    freeProductId: undefined,
  });

  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [allDay, setAllDay] = useState(false);

  const { data: productsData } = useProducts({
    status: "active",
    limit: 500,
    all: true,
  });
  const products = productsData?.data ?? [];

  useEffect(() => {
    if (open) {
      if (promotion) {
        const hasWindow = !!(promotion.startTime && promotion.endTime);
        setFormData({
          name: promotion.name,
          type: promotion.type,
          discountType: promotion.discountType,
          discountValue: promotion.discountValue,
          code: promotion.code ?? "",
          minOrderAmount: promotion.minOrderAmount ?? undefined,
          maxDiscount: promotion.maxDiscount ?? undefined,
          startTime: promotion.startTime ?? "",
          endTime: promotion.endTime ?? "",
          maxUses: promotion.maxUses ?? undefined,
          buyQuantity: promotion.buyQuantity ?? undefined,
          getQuantity: promotion.getQuantity ?? undefined,
          productId: promotion.productId ?? undefined,
          freeProductId: promotion.freeProductId ?? undefined,
        });
        setSelectedDays(promotion.daysOfWeek?.split(",").filter(Boolean) ?? []);
        setAllDay(promotion.type === "happy_hour" && !hasWindow);
      } else {
        setFormData({
          name: "",
          type: "discount_code",
          discountType: "percentage",
          discountValue: 10,
          code: "",
          minOrderAmount: undefined,
          maxDiscount: undefined,
          startTime: "",
          endTime: "",
          maxUses: undefined,
          buyQuantity: undefined,
          getQuantity: undefined,
          productId: undefined,
          freeProductId: undefined,
        });
        setSelectedDays([]);
        setAllDay(false);
      }
    }
  }, [open, promotion]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isBxgy = formData.type === "buy_x_get_y";

    if (isBxgy) {
      if (!formData.productId) {
        toast.error("Pick the product the customer has to buy");

        return;
      }
      if (!formData.freeProductId) {
        toast.error("Pick the product that comes free");

        return;
      }
      if (!formData.buyQuantity || formData.buyQuantity < 1) {
        toast.error("Buy quantity must be at least 1");

        return;
      }
      if (!formData.getQuantity || formData.getQuantity < 1) {
        toast.error("Get quantity must be at least 1");

        return;
      }
    }

    const data: CreatePromotionRequest = {
      name: formData.name!,
      type: formData.type!,
      discountType: isBxgy ? "fixed" : formData.discountType!,
      discountValue: isBxgy ? 0 : formData.discountValue!,
      code: formData.type === "discount_code" ? formData.code : undefined,
      minOrderAmount: formData.minOrderAmount,
      maxDiscount: formData.maxDiscount,
      startTime:
        formData.type === "happy_hour" && !allDay
          ? formData.startTime
          : undefined,
      endTime:
        formData.type === "happy_hour" && !allDay
          ? formData.endTime
          : undefined,
      daysOfWeek:
        formData.type === "happy_hour" && selectedDays.length > 0
          ? selectedDays.join(",")
          : undefined,
      maxUses: formData.maxUses,
      buyQuantity: isBxgy ? formData.buyQuantity : undefined,
      getQuantity: isBxgy ? formData.getQuantity : undefined,
      productId: isBxgy ? formData.productId : undefined,
      freeProductId: isBxgy ? formData.freeProductId : undefined,
    };

    onSubmit(data);
  };

  const toggleDay = (day: string) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {promotion ? "Edit Promotion" : "Create Promotion"}
          </DialogTitle>
          <DialogDescription>
            {promotion
              ? "Update promotion details"
              : "Create a new discount code or happy hour"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="promo-name">Name</Label>
            <Input
              id="promo-name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Summer Sale"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={formData.type}
              onValueChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  type: v as "discount_code" | "happy_hour" | "buy_x_get_y",
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="discount_code">Discount Code</SelectItem>
                <SelectItem value="happy_hour">Happy Hour</SelectItem>
                <SelectItem value="buy_x_get_y">Buy X Get Y Free</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.type !== "buy_x_get_y" && (
            <>
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select
                  value={formData.discountType}
                  onValueChange={(v) =>
                    setFormData((prev) => ({
                      ...prev,
                      discountType: v as "percentage" | "fixed",
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="promo-value">
                    {formData.discountType === "percentage"
                      ? "Discount %"
                      : "Discount Amount (cents)"}
                  </Label>
                  <Input
                    id="promo-value"
                    type="number"
                    min={1}
                    value={formData.discountValue || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        discountValue: parseInt(e.target.value) || 0,
                      }))
                    }
                    placeholder={
                      formData.discountType === "percentage"
                        ? "e.g., 10"
                        : "e.g., 500"
                    }
                    required
                  />
                </div>
                {formData.discountType === "percentage" && (
                  <div className="space-y-2">
                    <Label htmlFor="promo-max-discount">
                      Max Discount (cents)
                    </Label>
                    <Input
                      id="promo-max-discount"
                      type="number"
                      min={0}
                      value={formData.maxDiscount ?? ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          maxDiscount: e.target.value
                            ? parseInt(e.target.value)
                            : undefined,
                        }))
                      }
                      placeholder="e.g., 2000"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {formData.type === "buy_x_get_y" && (
            <div className="space-y-3 rounded-md border p-3">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="bxgy-buy-qty">Buy quantity</Label>
                  <Input
                    id="bxgy-buy-qty"
                    type="number"
                    min={1}
                    value={formData.buyQuantity ?? ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        buyQuantity: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      }))
                    }
                    placeholder="e.g., 2"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bxgy-get-qty">Get free quantity</Label>
                  <Input
                    id="bxgy-get-qty"
                    type="number"
                    min={1}
                    value={formData.getQuantity ?? ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        getQuantity: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      }))
                    }
                    placeholder="e.g., 1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bxgy-buy-product">Buy product</Label>
                <SearchablePicker
                  id="bxgy-buy-product"
                  items={products.map((p) => ({
                    id: p.id,
                    label: p.name,
                    description: p.category?.name ?? undefined,
                    trailing: p.sku,
                  }))}
                  value={formData.productId ?? null}
                  onChange={(id) =>
                    setFormData((prev) => ({ ...prev, productId: id }))
                  }
                  placeholder="Customer must buy…"
                  emptyMessage="No products match"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bxgy-free-product">Free product</Label>
                <SearchablePicker
                  id="bxgy-free-product"
                  items={products.map((p) => ({
                    id: p.id,
                    label: p.name,
                    description: p.category?.name ?? undefined,
                    trailing: p.sku,
                  }))}
                  value={formData.freeProductId ?? null}
                  onChange={(id) =>
                    setFormData((prev) => ({ ...prev, freeProductId: id }))
                  }
                  placeholder="…and gets this for free"
                  emptyMessage="No products match"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: set the buy and free products to the same item for a
                  classic 2-for-1 deal.
                </p>
              </div>
            </div>
          )}

          {formData.type === "discount_code" && (
            <div className="space-y-2">
              <Label htmlFor="promo-code">Promo Code</Label>
              <Input
                id="promo-code"
                value={formData.code}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    code: e.target.value.toUpperCase(),
                  }))
                }
                placeholder="SUMMER20"
                maxLength={50}
              />
            </div>
          )}

          {formData.type === "happy_hour" && (
            <>
              <div className="flex items-center gap-2 rounded-md border p-3">
                <Checkbox
                  id="promo-all-day"
                  checked={allDay}
                  onCheckedChange={(checked) => setAllDay(checked === true)}
                />
                <Label
                  htmlFor="promo-all-day"
                  className="flex-1 cursor-pointer font-normal"
                >
                  All day — discount applies on the selected days regardless of
                  time
                </Label>
              </div>
              {!allDay && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <TimePicker
                      value={formData.startTime || ""}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          startTime: value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <TimePicker
                      value={formData.endTime || ""}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          endTime: value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Days of Week</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <Button
                      key={day.value}
                      type="button"
                      variant={
                        selectedDays.includes(day.value) ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => toggleDay(day.value)}
                    >
                      {day.label.slice(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="promo-min-order">Min Order (cents)</Label>
              <Input
                id="promo-min-order"
                type="number"
                min={0}
                value={formData.minOrderAmount ?? ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    minOrderAmount: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  }))
                }
                placeholder="e.g., 1000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo-max-uses">Max Uses</Label>
              <Input
                id="promo-max-uses"
                type="number"
                min={1}
                value={formData.maxUses ?? ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    maxUses: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  }))
                }
                placeholder="e.g., 100"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {promotion ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
